import { useCallback, useMemo, useRef, type Dispatch, type SetStateAction } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiService } from '../services/api';
import type { ChangesColumnFilterKey } from '../components/ChangesTable';
import {
  buildAgencyNameToKeyMap,
  resolveAgencyKeysFromNames,
} from '../utils/buildAgencyNameToKeyMap';
import type { SourceSummary } from '../services/api';

type ColumnFilters = Record<ChangesColumnFilterKey, string[]>;

/**
 * Sincroniza filtro de agência da tabela do dashboard com preferências (agências de interesse).
 * - Hidrata coluna Agência a partir do que já está salvo.
 * - Persiste quando o usuário altera o filtro no dashboard.
 */
export function useSyncDashboardAgencyInterest(
  userId: number | undefined,
  preferredAgencies: string[],
  preferencesReady: boolean,
  agencySummaries: SourceSummary[],
) {
  const queryClient = useQueryClient();
  const lastPersistedSigRef = useRef<string | null>(null);
  const hydratedSigRef = useRef<string | null>(null);

  const nameToKey = useMemo(() => buildAgencyNameToKeyMap(agencySummaries), [agencySummaries]);

  const persistMutation = useMutation(
    (payload: { agency_names: string[]; agency_keys: string[] }) =>
      apiService.putPreferenceAgencySelection(userId || 1, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['preference-agency-selection', userId]);
        queryClient.invalidateQueries(['preferences', userId]);
        queryClient.invalidateQueries(['dashboard-stats']);
        queryClient.invalidateQueries(['dashboard-stats-year']);
      },
    },
  );

  const hydrateAgencyColumnFilter = useCallback(
    (setFilters: Dispatch<SetStateAction<ColumnFilters>>) => {
      if (!preferencesReady) return;

      const prefSig = preferredAgencies.join('|');
      if (hydratedSigRef.current === prefSig) return;

      if (preferredAgencies.length > 0) {
        setFilters((prev) => {
          const prevSig = prev.agency.join('|');
          if (prevSig === prefSig) return prev;
          return { ...prev, agency: [...preferredAgencies] };
        });
      }

      lastPersistedSigRef.current = prefSig;
      hydratedSigRef.current = prefSig;
    },
    [preferredAgencies, preferencesReady],
  );

  const persistAgencyColumnFilter = useCallback(
    (agencyNames: string[]) => {
      if (!preferencesReady || !userId) return;
      const sig = agencyNames.join('|');
      if (lastPersistedSigRef.current === sig) return;
      lastPersistedSigRef.current = sig;
      hydratedSigRef.current = sig;
      persistMutation.mutate({
        agency_names: agencyNames,
        agency_keys: resolveAgencyKeysFromNames(agencyNames, nameToKey),
      });
    },
    [nameToKey, persistMutation, preferencesReady, userId],
  );

  return { hydrateAgencyColumnFilter, persistAgencyColumnFilter };
}
